/**
 * useKeyboardScroll Hook
 * Automatically scrolls input into view when keyboard appears on mobile
 */

import { useEffect, RefObject } from 'react';

export const useKeyboardScroll = (inputRef: RefObject<HTMLInputElement | HTMLTextAreaElement | null>) => {
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const handleFocus = () => {
      // Small delay to let keyboard animation start
      setTimeout(() => {
        // Scroll input into view with more aggressive padding to account for keyboard
        const rect = input.getBoundingClientRect();
        const keyboardHeight = window.innerHeight * 0.4; // Assume keyboard takes 40% of screen
        const topPadding = 120; // Header + some padding
        const isInViewport = rect.top >= topPadding && rect.bottom <= (window.innerHeight - keyboardHeight);
        
        if (!isInViewport) {
          // Scroll to position input in the top third of the visible area
          window.scrollTo({
            top: window.scrollY + rect.top - (window.innerHeight * 0.25),
            behavior: 'smooth',
          });
        }
      }, 150);
    };

    input.addEventListener('focus', handleFocus);
    
    return () => {
      input.removeEventListener('focus', handleFocus);
    };
  }, [inputRef]);
};

/**
 * Auto-focus input when component mounts (for modals)
 */
export const useAutoFocus = (inputRef: RefObject<HTMLInputElement | HTMLTextAreaElement | null>, enabled: boolean = true) => {
  useEffect(() => {
    if (!enabled) return;
    
    const input = inputRef.current;
    if (!input) return;

    // Delay to let modal animation complete
    const timer = setTimeout(() => {
      input.focus();
    }, 300);

    return () => clearTimeout(timer);
  }, [inputRef, enabled]);
};

